import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import { dropTask, enqueueTask } from 'ember-concurrency';
import type EntryModel from 'garaje/models/entry';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type MetricsService from 'garaje/services/metrics';
import { Permission } from 'garaje/utils/ui-permissions';
import { alias } from 'macro-decorators';

interface BulkActionsMenuComponentSignature {
  Args: {
    menuHeight: string;
    canPrintBadges: boolean;
    isSelectAllIndeterminate: boolean;
    allSelected: boolean;
    deleteEntries: () => void;
    signOutEntriesTask: Task<void, [EntryModel[]]>;
    clearEntries: () => void;
    entries: EntryModel[];
    reprintBadge: (entries: EntryModel[]) => Promise<void>;
    printers: { id: string; name: string }[];
  };
}

export default class BulkActionsMenuComponent extends Component<BulkActionsMenuComponentSignature> {
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare metrics: MetricsService;

  @tracked displayPrinterSelectorModal = false;

  @alias('currentAdmin.canDeleteVisitor') canDeleteVisitor!: CurrentAdminService['canDeleteVisitor'];

  saveEntryTask = enqueueTask({ maxConcurrency: 10 }, async (entry: EntryModel) => {
    await entry.save();
    return entry;
  });

  get canSignOut(): boolean {
    // check whether some selected entries are sign-out-able (i.e., not all of them are already signed out)
    const areSomeEntriesSignOutable = this.args.entries.some((entry) => !entry.signOutTime);

    // can the current user edit all entries, regardless of whether they're their own?
    const userCanEditAllEntries = this.abilities.can('edit entries');

    // can the current user edit all selected entries? (i.e., they're the host for all, or an assistant of the host)
    const userCanEditAllSelectedEntries = this.args.entries.every((entry) => this.abilities.can('edit entry', entry));

    // sign-out button is available if some entries can be signed out and the user can edit all of the selected ones
    return areSomeEntriesSignOutable && (userCanEditAllEntries || userCanEditAllSelectedEntries);
  }

  get canDelete(): boolean {
    const areEmployeeInvites = this.args.entries.every((entry) => entry.employeeScreeningFlow);
    if (areEmployeeInvites) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_RESERVATION_DELETE);
    }
    return this.canDeleteVisitor;
  }

  get heightStyle(): SafeString {
    return htmlSafe(`top: -13px;height:${this.args.menuHeight}`);
  }

  reprintBadgeTask = dropTask(async (entries: EntryModel[]) => {
    await this.args.reprintBadge(entries);
    const entry_ids = entries.map((e) => parseInt(e.id, 10));
    this.metrics.trackEvent('Bulk Reprint Entry Badge Requested', {
      action_origin: 'visitor_log',
      entry_ids,
      entry_count: entries.length,
    });
    this.args.clearEntries();
  });

  @action
  signOut(): void {
    const toSave = this.args.entries.filter((entry) => !entry.signOutTime);
    void this.args.signOutEntriesTask.perform(toSave);
  }
}
